export type AxisId = "gca" | "compWay" | "skills";

export interface AxisScore {
  id: AxisId;
  value: number; // 0-1 for gca/compWay; level index 1-5 for skills
  threshold: number;
  pass: boolean;
  margin: number; // value - threshold
  closeCall: boolean; // |margin| ≤ 0.05 or === 0 for skills
  chroCorrection?: ChroCorrection;
}

export interface EvidenceRubricLevel {
  level: 1 | 2 | 3 | 4 | 5;
  description: string;
}

export interface EvidenceContribution {
  axis: AxisId;
  weight: number;
  weightRationale?: string;
}

export interface EvidenceSource {
  quote: string;
  citation: string;
  url?: string;
}

export interface ChroCorrection {
  originalScore: number;
  correctedScore: number;
  rationale: string;
  correctedAt: string;
  correctedBy: string;
}

export interface Evidence {
  id: string;
  name: string;
  description: string;
  rubric: EvidenceRubricLevel[];
  score: number | null;
  selectedLevel: 1 | 2 | 3 | 4 | 5;
  contributions: EvidenceContribution[];
  sources: EvidenceSource[];
  chroCorrection?: ChroCorrection;
}

export interface SensitivityPerturbation {
  evidenceId: string;
  delta: number;
  resultingScore: number;
  flipsVerdict: boolean;
}

export interface SensitivityResult {
  axis: AxisId;
  perturbations: SensitivityPerturbation[];
  fragilityRatio: string;
  fragile: boolean;
}

export interface SkillBullet {
  id: string;
  text: string;
  level: "L1" | "L2" | "L3" | "L4" | "L5";
  met: boolean;
  /** "framework": criterion defined by the skills framework.
   *  "observation": green/red flag extracted from the candidate profile. */
  kind?: "framework" | "observation";
  source?: { quote: string; citation: string };
  gapRationale?: string;
}

export interface FrameworkLevelMeta {
  title: string;
  description: string;
  /** Expected proficiency at this level, authored as "(N/5)" in the framework
   *  markdown. Drives the dot-rating rendered on each ladder card. */
  score?: number;
}

export interface FrameworkSkill {
  id: string;
  name: string;
  required: "L1" | "L2" | "L3" | "L4" | "L5";
  assigned: "L1" | "L2" | "L3" | "L4" | "L5";
  /** Required proficiency (0–5 dots) at the candidate's applied role level for
   *  this skill, sourced from the role × skill matrix ("(N/5)" per level in the
   *  framework markdown). Drives the navy polygon on the radar. */
  requiredDots?: number;
  /** Candidate's observed proficiency (0–5 dots) for this skill — independent of
   *  the LN level bucket. Defaults to `levelMeta[assigned].score` when the LLM
   *  does not provide an explicit dot count; overridable via the Ajustar panel. */
  assignedDots?: number;
  levelCoverage: Partial<
    Record<"L1" | "L2" | "L3" | "L4" | "L5" | "L6", { metCount: number; totalCount: number }>
  >;
  /** Per-role-level dot minimums for this skill (e.g., { L3: 3, L4: 4, L5: 5 }).
   *  Sourced from the LLM's levelMins output. Lets the UI flag "skill below
   *  minimum" when the candidate's assigned dots fall under the floor at the
   *  applied level — previously only enforced server-side, invisible on the
   *  review surface. */
  levelMins?: Partial<Record<"L1" | "L2" | "L3" | "L4" | "L5" | "L6", number>>;
  bullets: SkillBullet[];
  /** Title + description per level, sourced from the framework markdown so the
   * ladder can render "L4 · End-to-End Owner" + flavor text next to the bar. */
  levelMeta?: Partial<Record<"L1" | "L2" | "L3" | "L4" | "L5" | "L6", FrameworkLevelMeta>>;
  /** 2-3 sentence summary of why the candidate landed at `assigned` for this
   *  skill, in Portuguese. Sourced from the LLM's `positionFit.skills[].evidence`
   *  field — the prompt asks for "specific evidence from the profile". Surfaced
   *  on the skill row above the level ladder so the AI's per-skill reasoning
   *  is visible without expanding the per-criterion panel. Optional because
   *  older recommendations don't carry it. */
  rationale?: string;
}

/**
 * "Excepcionalidade" cutoffs — the bar at which a single very-high axis
 * lifts a candidate to `interview` even when one or both of the others
 * fail their pass threshold. Evolution of the original AND-rule
 * (gca.pass && compWay.pass && skills.pass): "to interview, the candidate
 * needs all three" → "to interview, the candidate needs all three OR a
 * single signal so strong it overrides the gap on the others."
 *
 * Cutoffs are deliberately well above the regular pass thresholds (gca
 * 0.60, compWay 0.65, skills = role target) so the exception only fires
 * for genuinely standout signals, not borderline ones. There is no veto
 * floor today (a hypothetical 0.86 GCA candidate with disastrous Comp
 * Way still gets through) — the rule's intent is to NEVER reject a clear
 * outlier. If the exception lane proves too permissive in practice, the
 * fix is to add `vetoFloors` here and intersect them in the aggregator.
 *
 * The hard cutoff has a known cliff edge: a 0.84 GCA loses to a 0.85 GCA
 * even though the underlying signal is essentially identical. The
 * `BORDERLINE_CUTOFFS` below soften that — see the comment there.
 */
export const EXCEPTIONAL_CUTOFFS = {
  /** GCA value (0–1) at or above which the GCA axis alone wins interview. */
  gca: 0.85,
  /** Comp Way value (0–1) at or above which Comp Way alone wins interview. */
  compWay: 0.85,
  /** Skills role-level (1–6) at or above which Skills alone wins interview. */
  skillsLevel: 5,
} as const;

/**
 * "Borderline" cutoffs — the *soft* lower edge of the exception band.
 *
 * Solves the cliff-edge problem on the continuous axes: today, a candidate
 * at GCA 0.84 silently disappears while 0.85 sails through, even though the
 * underlying signal is essentially the same. With borderline, anyone
 * landing in [0.80, 0.85) on GCA or Comp Way (and not already interviewed
 * by AND-pass or the hard exception lane) is still routed to interview,
 * but flagged as a `borderlineAxis` decision so the UI can render an amber
 * pill + tier-specific reason copy. The candidate flows through the same
 * recruiter queue as a regular interview (no separate triage queue today).
 *
 * Why no skills entry here: the L4 ↔ L5 transition isn't a smooth gradient
 * — it's a discrete role-level qualification, not a continuous score. A
 * "soft" interpretation of "almost L5" would need to inspect per-skill dot
 * counts against L5's per-skill floors, which couples the borderline lane
 * to FDE-specific data. Out of scope for this iteration; revisit if the
 * skills cliff (achieved=L4 with target=L5) becomes a problem in practice.
 *
 * Calibration: borderline collapses to interview for the calibration
 * baseline (decision === "interview"). The tier is purely a UI / triage
 * affordance, not a separate decision class.
 */
export const BORDERLINE_CUTOFFS = {
  /** GCA value at or above which (and below 0.85) the candidate is borderline. */
  gca: 0.8,
  /** Comp Way value at or above which (and below 0.85) the candidate is borderline. */
  compWay: 0.8,
} as const;

/** Display labels for axis ids — used in decision-reason copy and the UI
 *  pill subtitle. Kept here so server-side reason strings and client-side
 *  rendering pull the same names. */
export const AXIS_DISPLAY: Record<AxisId, string> = {
  gca: "GCA",
  compWay: "Comp Way",
  skills: "Skills",
};

/**
 * Pure rule: given the three axis scores, return the first axis whose
 * value crosses its `EXCEPTIONAL_CUTOFFS` bar, or null if none does.
 * Order is fixed (gca → compWay → skills) so multiple-exceptional cases
 * report a single deterministic strong axis. Both the production
 * aggregator (server) and the demo recommendation builder (shared) call
 * this so the exception lane never diverges between the two paths.
 */
export function findExceptionalAxis(
  gca: AxisScore,
  compWay: AxisScore,
  skills: AxisScore,
): AxisScore | null {
  if (gca.value >= EXCEPTIONAL_CUTOFFS.gca) return gca;
  if (compWay.value >= EXCEPTIONAL_CUTOFFS.compWay) return compWay;
  if (skills.value >= EXCEPTIONAL_CUTOFFS.skillsLevel) return skills;
  return null;
}

/**
 * Pure rule for the soft cutoff: returns the first axis whose value sits
 * in `[BORDERLINE_CUTOFFS.x, EXCEPTIONAL_CUTOFFS.x)`. Skills is intentionally
 * excluded (see `BORDERLINE_CUTOFFS` doc comment). Caller should only invoke
 * this AFTER the exception lane has already failed, so the upper bound is a
 * documentation invariant rather than a redundant guard — but we re-check
 * here defensively in case the call order ever shifts.
 */
export function findBorderlineAxis(gca: AxisScore, compWay: AxisScore): AxisScore | null {
  if (gca.value >= BORDERLINE_CUTOFFS.gca && gca.value < EXCEPTIONAL_CUTOFFS.gca) return gca;
  if (compWay.value >= BORDERLINE_CUTOFFS.compWay && compWay.value < EXCEPTIONAL_CUTOFFS.compWay)
    return compWay;
  return null;
}

/** Returns true when the decision payload represents an "advance to interview"
 *  call. Accepts the current `"interview"` value and the legacy `"hire"` value
 *  archived assessments still carry from before the hire→interview reframe.
 *  The flip side ("no-interview" / "no-hire" / anything else) is treated as
 *  not-interview, matching the binary pill the UI renders. */
export function isInterviewDecision(decision: unknown): boolean {
  return decision === "interview" || decision === "hire";
}

export interface ThreeAxisRecommendation {
  /** Screening verdict: "interview" = the recruiter should advance the
   *  candidate to a real interview, "no-interview" = pass for now. We
   *  intentionally don't promise hire vs no-hire — the tool screens against
   *  the framework's bar; final hiring stays a human call after the loop. */
  decision: "interview" | "no-interview";
  decisionReason: string;
  /** Set when the AND-rule failed but a single very-high axis lifted the
   *  candidate to `interview` via the exception lane. Carries the axis id
   *  + the value that crossed the cutoff so the UI can render an explicit
   *  "via excepcionalidade em GCA (0.92)" badge without re-parsing the
   *  reason string. Absent on plain AND-passes and on no-interview. */
  exceptionalAxis?: { id: AxisId; value: number };
  /** Set when neither AND-pass nor the exception lane fired, but a single
   *  axis sits in the soft band [0.80, 0.85). The candidate is still
   *  recommended (`decision === "interview"`) but the UI renders an amber
   *  "borderline" pill + tier-specific reason copy. Mutually exclusive with
   *  `exceptionalAxis` — the hard exception always wins. */
  borderlineAxis?: { id: AxisId; value: number };
  /**
   * Set when the inferred market level (from career-history Call 6) sits at
   * least one CGL band above the candidate's skill-based assigned level with
   * sufficient confidence (>= 0.7). When present, decision is forced to
   * "no-interview" — overrides exception/borderline lanes. Captures the level
   * mismatch the UI surfaces under the decision pill.
   *
   * Gate conditions (all must hold):
   *  1. `assessment.marketLevel` is set (legacy rows without it skip the gate).
   *  2. `assessment.marketLevel.confidence >= 0.7`
   *  3. `assessment.marketLevel.cgl > skillsAxis.value` (1+ CGL bands above).
   *
   * Track-agnostic: fires whether market level is IC or MGMT. Cross-track
   * cases (MGMT market level vs IC skill axis) set `crossTrack: true` and
   * include a qualifier in the reason copy but don't change the outcome.
   */
  marketImbalance?: {
    marketCgl: number;
    marketDisplayName: string;
    marketCglDisplayName: string;
    marketTrack: "IC" | "MGMT";
    marketConfidence: number;
    skillsCgl: number;
    skillsCglDisplayName: string;
    bandsAbove: number;
    /** True when the market-level track differs from "IC" (the skill axis is
     *  always implicitly IC since it tracks framework skills). Drives a small
     *  qualifier in the reason copy ("transição de carreira potencial") but
     *  does NOT gate behavior. */
    crossTrack: boolean;
  };
  axes: AxisScore[]; // order: gca, compWay, skills
  evidences: Evidence[];
  sensitivity: SensitivityResult[];
  frameworkSkills: FrameworkSkill[];
  appliedLevel: "L1" | "L2" | "L3" | "L4" | "L5" | "L6";
  levelRationale: string;
  /** Canonical discipline identifier (e.g. "forward-deployed"). Resolves via
   *  shared/labs/disciplines/registry.ts → DisciplineSpec. Required for any
   *  recommendation built after 2026-05-12; older snapshots may omit this
   *  field and consumers must default to "forward-deployed". */
  disciplineSlug?: string;
  /** Per-skill hire floors used to compute this recommendation. Discipline-
   *  agnostic shape (Record<level, { perSkill, sum }>). The canonical field.
   *  Snapshots in `labs_evaluations.assessment` written before 2026-05-13
   *  stored the same data under `fdeFloors`; `readRecommendationFloors`
   *  handles that fallback at runtime. */
  floors?: import("./disciplines/types").ResolvedFloors;
}

/**
 * Read the floors a recommendation was computed against. Prefers the
 * canonical `floors` field. Falls back to the legacy `fdeFloors` key for
 * jsonb snapshots in DB that predate the discipline registry — that field
 * is no longer in the TypeScript type, so the lookup is done via a runtime
 * cast. Returns undefined when neither is present; callers should then
 * fall back to the registry default for the recommendation's discipline.
 */
export function readRecommendationFloors(
  rec: Partial<ThreeAxisRecommendation>,
): import("./disciplines/types").ResolvedFloors | undefined {
  if (rec.floors) return rec.floors;
  const legacy = (rec as { fdeFloors?: unknown }).fdeFloors;
  // ResolvedFloors is a plain object keyed by level integer. Reject anything
  // else (null, arrays, primitives) so a corrupt jsonb payload can't propagate
  // an empty array downstream — `{ ...constants, ...[] }` spreads to a record
  // with no level keys, which would then be cached as the canonical `floors`
  // on the next write.
  if (!legacy || typeof legacy !== "object" || Array.isArray(legacy)) return undefined;
  return legacy as import("./disciplines/types").ResolvedFloors;
}

export function parseLevel(l: "L1" | "L2" | "L3" | "L4" | "L5"): 1 | 2 | 3 | 4 | 5 {
  return Number(l.slice(1)) as 1 | 2 | 3 | 4 | 5;
}

/** Shape submitted when the CHRO saves an Ajustar panel. `evidenceId` is used
 *  as the generic target identifier — for axis corrections it holds the axis id
 *  ("gca" | "compWay"), for skills the framework skill id. The `kind` hint is
 *  optional; when omitted, useCorrectionHandler falls back to looking up the
 *  id against evidences / axes / frameworkSkills in that order. */
export interface CorrectionInput {
  evidenceId: string;
  newScore: number;
  rationale: string;
  kind?: "evidence" | "axis" | "skill";
  /** `"park"` (default) queues the correction for batch re-calibration; no
   *  agent fires on submit. `"preview"` runs the agent immediately but parks
   *  the resulting patch in `awaiting_review`. `"auto"` runs the agent and
   *  commits on converge — retained for back-compat but no longer the UI
   *  default. */
  policy?: "auto" | "preview" | "park";
  /** For `kind === "axis"`: which sub-target the correction should rebalance.
   *  `"weights"` rebalances evidence weights; `"threshold"` shifts the pass/fail
   *  cut. `"evidence"` would route through an evidence row — currently not
   *  surfaced in the UI. */
  axisSubKind?: "evidence" | "weights" | "threshold";
}

export type CorrectionCallback = (
  input: CorrectionInput,
) => Promise<{ correctionId: string } | undefined>;
